// 영웅 선택(c022 D4a): 마을 HUD 줄(골드·가방 옆)의 「영웅」 버튼과 그 아래에 펼치는 작은 고르기 창.
// 마을에서만 보이고(던전·결과·일시정지에서는 숨김) 플레이 영역 가운데를 가리지 않게 화면 왼쪽 위 HUD 줄 바로 아래에 붙는다.
// 게임 상태는 읽기만 하고 변경은 GameCommand(selectHero)로만 한다. 결과는 Ev.HeroSelect → view.heroId.
import { HEROES_90D } from '../shared/content/heroes';
import { PLAYABLE_HEROES, type GameCommand, type GameView, type PlayableHeroId } from '../shared/types';
import { FONT, el } from './dom';
import { HUD_ICON_PX, iconOver } from './uiArt';
import { viewH, viewRect } from '../shared/viewport';
import { assetUrl } from '../pwa/assetMap';
import { characterEntry, type CharacterEntry } from './characterEntry';
import { AWAKEN_SKILL_ID, skillIconPath } from './skillIcons';
import { skillTileImg } from './skillIconsDom';

const STYLE_ID = 'dh-hero-picker-style';
const CSS =
  '.dh-hp-panel{position:absolute;left:0;top:calc(100% + 8px);display:none;flex-direction:column;gap:8px;overflow-y:auto;width:300px;max-width:min(300px,calc(100vw - 24px));box-sizing:border-box;padding:10px;' +
  'border:2px solid #F2C14E;border-radius:14px;background:linear-gradient(180deg,rgba(26,34,62,.97),rgba(12,17,34,.97));box-shadow:0 6px 18px rgba(0,0,0,.6);pointer-events:auto;z-index:5}' +
  '.dh-hp-panel[data-open="1"]{display:flex}' +
  // 아래 공간이 모자라면(가로 폰: HUD 줄이 화면 아래쪽) 위로 펼친다(퀘스트 카드 위, 플레이 영역 가운데는 피한다)
  '.dh-hp-panel[data-up="1"]{top:auto;bottom:calc(100% + 8px)}' +
  '.dh-hp-head{display:flex;align-items:center;justify-content:space-between;color:#ffe07a;font:900 16px ' + FONT + '}' +
  '.dh-hp-x{min-width:40px;min-height:var(--dh-btn-min,40px);border-radius:10px;border:1.5px solid rgba(242,193,78,.5);background:#151d38;color:#fff;font:800 16px ' + FONT + ';cursor:pointer}' +
  // 터치 기기: 닫기 단추 겉 크기 최소 44px(출시 게이트 RG-008). 배율로 줄어드는 만큼 --dh-btn-min이 키우므로 둘 중 큰 값
  '@media (pointer:coarse){.dh-hp-x{min-width:max(44px,var(--dh-btn-min,44px));min-height:max(44px,var(--dh-btn-min,44px))}}' +
  '.dh-hp-card{flex:0 0 auto;display:flex;align-items:center;gap:10px;min-height:var(--dh-btn-min,56px);padding:8px 10px;border-radius:12px;border:1.5px solid rgba(242,193,78,.38);' +
  'background:linear-gradient(180deg,#24305a,#151d38);color:#fff;text-align:left;cursor:pointer;font:700 14px ' + FONT + '}' +
  '.dh-hp-card[aria-pressed="true"]{border-color:#fff1b8;box-shadow:0 0 14px rgba(242,193,78,.45),inset 0 1px 0 rgba(255,255,255,.25)}' +
  '.dh-hp-sw{position:relative;flex:0 0 auto;width:40px;height:40px;border-radius:9px;border:2px solid rgba(255,255,255,.7);overflow:hidden}' +
  '.dh-hp-sw img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}' +
  '.dh-hp-txt{display:flex;flex-direction:column;gap:2px;min-width:0}' +
  '.dh-hp-name{font:900 16px ' + FONT + '}' +
  '.dh-hp-sub{font:600 12px ' + FONT + ';color:#cfd6ee;white-space:normal;word-break:keep-all;overflow-wrap:anywhere}' +
  '.dh-hp-tag{margin-left:auto;font:800 11px ' + FONT + ';color:#2a1d05;background:#ffd76a;border-radius:6px;padding:2px 6px;white-space:nowrap}' +
  '.dh-hp-sec{margin-top:4px;color:#ffe07a;font:900 14px ' + FONT + '}' +
  '.dh-hp-face{position:relative;flex:0 0 auto;width:48px;height:48px;border-radius:9px;border:2px solid rgba(255,255,255,.7);overflow:hidden;background:#1a2238}' +
  '.dh-hp-face img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:50% 15%}' +
  '.dh-hp-skills{font:600 11px ' + FONT + ';color:#e4d49a;line-height:1.35;white-space:normal}' +
  '.dh-hp-soon{position:relative;opacity:.82;cursor:default}' +
  '.dh-hp-soon .dh-hp-tag{background:#8d97b8;color:#10162a}' +
  '.dh-hp-chars{min-height:var(--dh-btn-min,44px);border-radius:10px;border:1.5px solid rgba(242,193,78,.7);background:linear-gradient(#24305a,#151d38);color:#ffe07a;font:800 14px ' + FONT + ';cursor:pointer}' +
  '.dh-hp-note{font:600 11px ' + FONT + ';color:#b9c2de;line-height:1.35}';

/** 영웅 카드 문구: 이름 · 직업 · 무기 / 역할(content/heroes.ts) */
export function heroCardText(id: PlayableHeroId): { name: string; sub: string; color: string } {
  const h = HEROES_90D.find((x) => x.id === id)!;
  return { name: h.name, sub: h.job + ' · ' + h.weapon + ' · ' + h.role, color: h.colors[0] };
}

/** 직업 소개 카드 5장(플레이 가능 2 + 준비 중 3). 스킬 이름은 HEROES_90D 앞쪽 3개(기본기), 각성은 awaken */
export interface ClassCard { id: string; name: string; job: string; weapon: string; role: string; skills: string[]; awaken: string; ready: boolean }
export function classCards(): ClassCard[] {
  const out: ClassCard[] = [];
  for (const id of ['kael', 'lyra', 'seren', 'brann']) {
    const h = HEROES_90D.find((x) => x.id === id);
    if (!h) continue;
    out.push({ id, name: h.name, job: id === 'brann' ? '수호자' : h.job, weapon: h.weapon, role: h.role, skills: h.skills.filter((k) => k.kind === 'base' || k.kind === 'active').slice(0, 3).map((k) => k.name), awaken: h.awaken, ready: id === 'kael' || id === 'lyra' });
  }
  // 루멘은 heroes에 없다(docs/CHARACTER-PRD.md 직업 5): 사제, 지팡이·성서, 치유·보호·신성 딜
  out.push({ id: 'lumen', name: '루멘', job: '사제', weapon: '지팡이·성서', role: '치유·보호·신성 딜', skills: ['성광탄', '치유의 빛', '축복'], awaken: '성광 강림', ready: false });
  return out;
}

export class HeroPicker {
  private readonly btn: HTMLButtonElement;
  private readonly btnLabel: HTMLSpanElement;
  private readonly panel: HTMLDivElement;
  private readonly cards: { id: PlayableHeroId; b: HTMLButtonElement; tag: HTMLSpanElement }[] = [];
  private open = false;
  private shownHero: PlayableHeroId | null = null;
  /** 캐릭터 모드(CHARACTER-PRD §3.4): 이 버튼이 영웅 고르기 대신 캐릭터 선택 화면을 연다(직업은 캐릭터마다 고정) */
  private readonly chars: CharacterEntry | null = characterEntry();
  private charsDirty = true;

  /** external: 오른쪽 위 메뉴 줄(menuBar.ts)의 「영웅」 그림 단추가 연다. 이때 자기 단추는 숨기고 row(단추 아래 자리)에 창만 펼친다 */
  private readonly external: boolean;
  constructor(row: HTMLElement, private readonly view: GameView, private readonly send: (c: GameCommand) => void, opts: { external?: boolean } = {}) {
    this.external = opts.external === true;
    if (!document.getElementById(STYLE_ID)) {
      const st = document.createElement('style');
      st.id = STYLE_ID;
      st.textContent = CSS;
      document.head.appendChild(st);
    }
    this.btn = el('button', '', row);
    this.btn.type = 'button';
    this.btn.className = 'dh-r-bag dh-r-ico';
    this.btn.dataset.testid = 'rpg-hero';
    // HUD 정리: 영웅 바꾸기 그림(Codex image_gen btn-hero-pick, 이름은 aria-label·툴팁). 받지 못하면 사람 선 모양이 대신 보인다
    iconOver(el('div', 'width:' + HUD_ICON_PX + 'px;height:' + HUD_ICON_PX + 'px;display:grid;place-items:center;pointer-events:none;', this.btn), 'btn-hero-pick', HUD_ICON_PX,
      '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"/></svg>');
    this.btn.dataset.sfx = 'event';
    this.btn.setAttribute('aria-haspopup', 'dialog');
    this.btnLabel = el('span', '', this.btn, '영웅');
    this.btn.addEventListener('click', () => { if (this.chars) this.chars.open(); else this.setOpen(!this.open); });
    if (this.chars) { this.btn.setAttribute('aria-haspopup', 'dialog'); this.btn.dataset.testid = 'rpg-character'; this.chars.onChange(() => { this.charsDirty = true; }); }
    if (this.external) this.btn.style.display = 'none';

    this.panel = el('div', '', row);
    this.panel.className = 'dh-hp-panel';
    this.panel.dataset.testid = 'hero-picker';
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-label', '영웅 · 직업');
    const head = el('div', '', this.panel);
    head.className = 'dh-hp-head';
    el('span', '', head, '영웅 · 직업');
    const x = el('button', '', head, '✕');
    x.type = 'button'; x.className = 'dh-hp-x'; x.setAttribute('aria-label', '닫기'); x.dataset.testid = 'hero-picker-close';
    x.addEventListener('click', () => this.setOpen(false));
    for (const id of PLAYABLE_HEROES) {
      const t = heroCardText(id);
      const b = el('button', '', this.panel);
      b.type = 'button'; b.className = 'dh-hp-card'; b.dataset.testid = 'hero-card-' + id; b.dataset.sfx = 'event';
      // 영웅 색 칸 위에 그 영웅 각성 그림(2026-10-27 스킬 아이콘 회차). 그림이 없으면 색 칸만 남는다
      const sw = el('span', '', b); sw.className = 'dh-hp-sw'; sw.style.background = t.color;
      const awaken = skillIconPath(id, AWAKEN_SKILL_ID);
      if (awaken) skillTileImg(sw, awaken);
      const txt = el('span', '', b); txt.className = 'dh-hp-txt';
      el('span', '', txt, t.name).className = 'dh-hp-name';
      el('span', '', txt, t.sub).className = 'dh-hp-sub';
      const cc = classCards().find((c) => c.id === id);
      if (cc) el('span', '', txt, '스킬 ' + cc.skills.join(' · ') + ' · 각성 ' + cc.awaken).className = 'dh-hp-skills';
      const tag = el('span', '', b, '플레이 중'); tag.className = 'dh-hp-tag';
      b.addEventListener('click', () => {
        if ((this.view.heroId ?? 'kael') !== id) this.send({ type: 'selectHero', hero: id });
        this.setOpen(false);
      });
      this.cards.push({ id, b, tag });
    }
    // 직업 소개(PRD 5절): 아직 못 고르는 세 직업. 누를 수 없다(aria-disabled)
    el('div', '', this.panel, '직업 소개').className = 'dh-hp-sec';
    for (const c of classCards()) {
      if (c.ready) continue;
      const card = el('div', '', this.panel);
      card.className = 'dh-hp-card dh-hp-soon'; card.dataset.testid = 'class-card-' + c.id; card.setAttribute('aria-disabled', 'true');
      const face = el('span', '', card); face.className = 'dh-hp-face';
      const img = document.createElement('img'); img.alt = c.job + ' ' + c.name; img.decoding = 'async'; img.loading = 'lazy'; img.src = assetUrl('ui/char/' + c.id + '.webp') /* 캐릭터 생성 화면(#224)과 같은 직업 초상 */;
      img.addEventListener('error', () => img.remove()); face.appendChild(img);
      const txt = el('span', '', card); txt.className = 'dh-hp-txt';
      el('span', '', txt, c.name).className = 'dh-hp-name';
      el('span', '', txt, c.job + ' · ' + c.weapon + ' · ' + c.role).className = 'dh-hp-sub';
      el('span', '', txt, '스킬 ' + c.skills.join(' · ') + ' · 각성 ' + c.awaken).className = 'dh-hp-skills';
      el('span', '', card, '준비 중').className = 'dh-hp-tag';
    }
    // 무기 칸 안내: 영웅마다 자기 무기 칸이 따로 있다(lyra-weapon-slot)
    el('div', '', this.panel, '레벨·스킬·무기는 영웅마다 따로, 골드·방어구·장신구·지역은 함께 써요.').className = 'dh-hp-note';
    // 캐릭터 모드(#224, characterEntry())에서는 「영웅」 단추가 이 창 대신 캐릭터 선택·생성 화면을 바로 연다(직업 카드·생성은 그 화면에 있다).
    // 그래서 이 창 안에 따로 진입 단추를 두지 않는다(가짜 단추 금지)
    window.addEventListener('keydown', (e) => { if (this.open && e.code === 'Escape') this.setOpen(false); });
  }

  /** 메뉴 줄 단추가 부른다 */
  toggle(): void { if (this.chars) { this.chars.open(); return; } this.setOpen(!this.open); } // 캐릭터 모드: 「영웅」 메뉴 단추가 선택 화면을 연다
  close(): void { this.setOpen(false); }

  private setOpen(open: boolean): void {
    if (open === this.open) return;
    this.open = open;
    this.panel.dataset.up = '0';
    this.panel.dataset.open = open ? '1' : '0';
    this.btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!open) return;
    // 열 때 한 번 잰다: 아래로 넘치고 위에 더 넓은 공간이 있으면 위로 펼친다.
    // 세로 화면은 앱이 90도 돌려 가로로 보여 주므로(viewport.ts) 사각형·높이를 모두 게임 화면 좌표로 바꿔 잰다(c022: 390x844에서 카드가 화면 밖으로 열렸다)
    const r = viewRect(this.panel), b = viewRect(this.external ? (this.panel.parentElement ?? this.btn) : this.btn), h = viewH();
    const up = r.bottom > h - 4 && b.top - 8 > h - b.bottom;
    if (up) this.panel.dataset.up = '1';
    // 그래도 넘치면(아주 낮은 가로 화면) 높이를 줄이고 안에서 스크롤한다. 부모가 배율(transform)이라 설계 px로 바꾼다
    const scale = this.panel.offsetHeight > 0 ? r.height / this.panel.offsetHeight : 1;
    const avail = (up ? b.top : h - b.bottom) - 16;
    this.panel.style.maxHeight = avail < r.height ? Math.max(80, avail / scale) + 'px' : '';
  }

  /** 매 프레임: on = 마을 HUD 줄이 보이는 때. 값이 바뀔 때만 DOM을 쓴다 */
  update(on: boolean): void {
    if (!on) { this.setOpen(false); return; }
    if (this.chars) {
      if (!this.charsDirty) return;
      this.charsDirty = false;
      const label = this.chars.label();
      this.btnLabel.textContent = '캐릭터 · ' + label;
      this.btn.setAttribute('aria-label', '캐릭터 선택, 지금 ' + label);
      this.btn.setAttribute('data-tip', '캐릭터 선택 · ' + label);
      this.btn.dataset.unnamed = this.chars.unnamed() ? '1' : '0';
      return;
    }
    const hero = this.view.heroId ?? 'kael';
    if (hero === this.shownHero) return;
    this.shownHero = hero;
    const t = heroCardText(hero);
    this.btnLabel.textContent = '영웅 · ' + t.name;
    this.btn.setAttribute('aria-label', '영웅 선택, 지금 ' + t.name);
    this.btn.setAttribute('data-tip', '영웅 · ' + t.name);
    for (const c of this.cards) {
      const cur = c.id === hero;
      c.b.setAttribute('aria-pressed', cur ? 'true' : 'false');
      c.tag.hidden = !cur;
    }
  }
}
